'use client';

import {
  cubicBezier,
  motion,
  MotionValue,
  useScroll,
  useTransform,
} from 'motion/react';
import React, { useEffect, useRef, useState } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { VideoMedia } from '@/components/VideoMedia';
import type { Media, MediaMosaic as MediaMosaicProps } from '@/payload-types';
import { cn } from '@/utils/cn';

import { BlockSettings } from '../BlockSettings';
import styles from './block.module.scss';

interface MosaicItemConfig {
  aspectRatio: 'square' | 'portrait' | 'standard' | 'landscape' | 'none';
  x: number;
  y: number;
}

const MediaMosaicItem: React.FC<{
  amountOfItems: number;
  index: number;
  media: any;
  zIndex: number;
  scrollYProgress: MotionValue<number>;
}> = ({ amountOfItems, index, media, scrollYProgress, zIndex }) => {
  const [isMobile, setIsMobile] = useState(false);

  const mosaicItemConfigThree: MosaicItemConfig[] = isMobile
    ? [
        { aspectRatio: 'portrait', x: 0, y: 0 },
        { aspectRatio: 'landscape', x: -15, y: -60 },
        { aspectRatio: 'standard', x: 15, y: 60 },
      ]
    : [
        { aspectRatio: 'portrait', x: 0, y: 0 },
        { aspectRatio: 'portrait', x: -115, y: 0 },
        { aspectRatio: 'portrait', x: 115, y: 0 },
      ];

  const mosaicItemConfigFour: MosaicItemConfig[] = [
    { aspectRatio: 'square', x: 0, y: 0 },
    { aspectRatio: 'square', x: -115, y: 0 },
    { aspectRatio: 'landscape', x: 0, y: -40 },
    { aspectRatio: 'portrait', x: 115, y: -60 },
  ];

  const mosaicItemConfig =
    amountOfItems === 3 ? mosaicItemConfigThree : mosaicItemConfigFour;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 1279px)').matches);
    };

    checkMobile();

    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const x = useTransform(
    scrollYProgress,
    [0, 1],
    [`${mosaicItemConfig[index].x}%`, `0%`],
    { ease: cubicBezier(0.17, 0.67, 0.83, 0.67) }
  );
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [`${mosaicItemConfig[index].y}%`, `0%`],
    { ease: cubicBezier(0.17, 0.67, 0.83, 0.67) }
  );

  const MediaComponent =
    media.mediaType === 'image' && media.image ? (
      <ImageMedia
        aspectRatio={mosaicItemConfig[index].aspectRatio}
        image={media.image}
        responsiveWidths={
          mosaicItemConfig[index].aspectRatio === 'square'
            ? {
                mobile: 343,
                desktop: 512,
                largeDesktop: 2256,
              }
            : mosaicItemConfig[index].aspectRatio === 'portrait'
              ? {
                  mobile: 343,
                  desktop: 512,
                  largeDesktop: 800,
                }
              : {
                  mobile: 343,
                  desktop: 1352,
                  largeDesktop: 2256,
                }
        }
      />
    ) : media.mediaType === 'video' && media.videoEmbedUrl ? (
      <VideoMedia
        aspectRatio={mosaicItemConfig[index].aspectRatio}
        videoPoster={media.videoPoster as Media}
        videoEmbedUrl={media.videoEmbedUrl}
        videoIsBackground={media.videoIsBackground as boolean}
        videoLoops={media.videoLoops as boolean}
      />
    ) : null;

  if (!MediaComponent) return null;

  return (
    <motion.div
      className={cn(styles.item, styles[mosaicItemConfig[index].aspectRatio])}
      style={{
        zIndex,
        x,
        y,
        willChange: 'transform',
      }}
    >
      {MediaComponent}
    </motion.div>
  );
};

export const MediaMosaic: React.FC<MediaMosaicProps> = (props) => {
  const {
    backgroundColor,
    individualSpacing,
    medias,
    spacing,
    spacingBottom,
    spacingTop,
  } = props;

  const mosaicRef = useRef<HTMLDivElement | null>(null);

  // Track scroll progress relative to this specific container
  const { scrollYProgress } = useScroll({
    target: mosaicRef,
    offset: ['start 100vh', 'center center'],
  });

  if (!medias || !Array.isArray(medias) || medias.length === 0) {
    return null;
  }

  return (
    <BlockSettings
      backgroundColor={backgroundColor}
      individualSpacing={individualSpacing}
      spacing={spacing}
      spacingBottom={spacingBottom}
      spacingTop={spacingTop}
    >
      <div
        ref={mosaicRef}
        className={cn(styles.wrapper, styles[`items--${medias.length}`])}
      >
        <div className={cn(styles.items, styles[`items-${medias.length}`])}>
          {medias &&
            medias
              .slice(0, 4)
              .map((media, index: number) => (
                <MediaMosaicItem
                  key={index}
                  amountOfItems={medias.length}
                  index={index}
                  media={media}
                  zIndex={(medias && medias.length - index) || 0}
                  scrollYProgress={scrollYProgress}
                />
              ))}
        </div>
      </div>
    </BlockSettings>
  );
};
